import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Play, Pause, SkipBack, SkipForward, ArrowCounterClockwise, SpeakerHigh, SpeakerSlash,
} from '@phosphor-icons/react';

/**
 * Video-style walkthrough generated from a help answer's text: a title card,
 * one slide per step (numbered lines or bullets), then a closing card. Plays
 * automatically with optional spoken narration (the browser's built-in speech).
 */

const STEP_RE = /^\s*(?:\d+[.)]|[-•])\s+(.*)$/;

function splitSentences(text) {
  return (text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || []).map((s) => s.trim()).filter(Boolean);
}

export function buildSlides(title, answer, linkLabel) {
  const lines = (answer || '').split('\n');
  const steps = [];
  const before = [];
  const after = [];
  lines.forEach((line) => {
    const m = line.match(STEP_RE);
    if (m) steps.push(m[1].trim());
    else if (line.trim()) (steps.length ? after : before).push(line.trim());
  });

  const slides = [{ kind: 'title', text: title }];
  if (steps.length) {
    if (before.length) slides.push({ kind: 'note', text: before.join(' ') });
    steps.forEach((text, i) => slides.push({ kind: 'step', n: i + 1, total: steps.length, text }));
    if (after.length) slides.push({ kind: 'note', text: after.join(' ') });
  } else {
    // No list in the answer — a short sentence or two per slide.
    const notes = [];
    splitSentences(answer || '').forEach((s) => {
      if (notes.length && notes[notes.length - 1].length + s.length < 90) notes[notes.length - 1] += ` ${s}`;
      else notes.push(s);
    });
    notes.slice(0, 8).forEach((text) => slides.push({ kind: 'note', text }));
  }
  slides.push({ kind: 'end', text: linkLabel ? `Done! Use the "${linkLabel}" button to get started.` : 'That’s it — you’re all set.' });
  return slides;
}

function speechText(slide) {
  if (slide.kind === 'step') return `Step ${slide.n}. ${slide.text}`.replace(/→/g, ', then ');
  return slide.text.replace(/→/g, ', then ');
}

function slideDuration(slide) {
  return Math.min(10000, 2600 + slide.text.length * 55);
}

// One menu label: up to four capitalised words, e.g. "Update Status", "Module Access".
const LABEL = "[A-Z][\\w&'-]*(?: [A-Z&][\\w&'-]*){0,3}";
const RICH_RE = new RegExp(`("[^"]+"|${LABEL}(?: → ${LABEL})+)`, 'g');

/** Highlight "A → B" navigation paths and "quoted" labels. */
function RichText({ text }) {
  const parts = text.split(RICH_RE).filter(Boolean);
  return parts.map((part, i) => {
    if (part.includes(' → ')) {
      const crumbs = part.split(' → ');
      return (
        <span key={i} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', margin: '0 2px' }}>
          {crumbs.map((c, j) => (
            <React.Fragment key={j}>
              <span style={{ background: '#2563EB', color: 'white', padding: '2px 10px', borderRadius: '4px', fontWeight: 600 }}>{c}</span>
              {j < crumbs.length - 1 && <span style={{ color: '#93C5FD' }}>→</span>}
            </React.Fragment>
          ))}
        </span>
      );
    }
    if (part.startsWith('"') && part.endsWith('"')) {
      return <span key={i} style={{ color: '#FDE68A', fontWeight: 600 }}>{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const KEYFRAMES = '@keyframes helpWtIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }';

function HelpWalkthrough({ title, answer, linkLabel, autoPlay = true }) {
  const slides = useMemo(() => buildSlides(title, answer, linkLabel), [title, answer, linkLabel]);
  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [voice, setVoice] = useState(canSpeak);
  const timerRef = useRef(null);
  const runRef = useRef(0);

  const stopAll = useCallback(() => {
    runRef.current += 1;
    clearTimeout(timerRef.current);
    if (canSpeak) window.speechSynthesis.cancel();
  }, [canSpeak]);

  useEffect(() => {
    setIndex(0);
    setPlaying(autoPlay);
  }, [slides, autoPlay]);

  useEffect(() => {
    stopAll();
    if (!playing) return undefined;
    const run = runRef.current;
    const slide = slides[index];
    const next = () => {
      if (run !== runRef.current) return;
      if (index < slides.length - 1) setIndex((i) => i + 1);
      else setPlaying(false);
    };
    if (voice && canSpeak) {
      const u = new SpeechSynthesisUtterance(speechText(slide));
      u.rate = 0.95;
      u.onend = () => { timerRef.current = setTimeout(next, 700); };
      u.onerror = () => { timerRef.current = setTimeout(next, slideDuration(slide)); };
      window.speechSynthesis.speak(u);
    } else {
      timerRef.current = setTimeout(next, slideDuration(slide));
    }
    return stopAll;
  }, [index, playing, voice, slides, canSpeak, stopAll]);

  useEffect(() => stopAll, [stopAll]);

  const go = (i) => setIndex(Math.max(0, Math.min(slides.length - 1, i)));
  const restart = () => { setIndex(0); setPlaying(true); };
  const slide = slides[index];
  const finished = !playing && index === slides.length - 1;

  const btn = {
    background: 'transparent', border: 'none', color: 'white', cursor: 'pointer',
    padding: '4px', display: 'flex', alignItems: 'center',
  };

  return (
    <div style={{ width: '100%', border: '1px solid #1F2937', background: '#0F172A', userSelect: 'none' }} data-testid="help-walkthrough">
      <style>{KEYFRAMES}</style>
      <div
        style={{
          position: 'relative', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '6%', background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)', color: 'white', overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={() => (finished ? restart() : setPlaying((p) => !p))}
      >
        <div key={index} style={{ animation: 'helpWtIn 0.45s ease-out', width: '100%', textAlign: slide.kind === 'step' ? 'left' : 'center' }}>
          {slide.kind === 'title' && (
            <>
              <p style={{ fontSize: 'clamp(11px, 1.6vw, 13px)', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#93C5FD', marginBottom: '10px' }}>
                How to
              </p>
              <h3 style={{ fontSize: 'clamp(18px, 3.2vw, 30px)', fontWeight: 700, lineHeight: 1.25, margin: 0 }}>{slide.text}</h3>
            </>
          )}
          {slide.kind === 'step' && (
            <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
              <div
                style={{
                  flexShrink: 0, width: 'clamp(36px, 6vw, 56px)', height: 'clamp(36px, 6vw, 56px)', borderRadius: '50%',
                  background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'clamp(16px, 2.6vw, 24px)', fontWeight: 700, boxShadow: '0 0 0 6px rgba(37,99,235,0.25)',
                }}
              >
                {slide.n}
              </div>
              <div>
                <p style={{ fontSize: 'clamp(10px, 1.4vw, 12px)', color: '#93C5FD', margin: '0 0 6px' }}>Step {slide.n} of {slide.total}</p>
                <p style={{ fontSize: 'clamp(14px, 2.4vw, 22px)', lineHeight: 1.5, margin: 0 }}><RichText text={slide.text} /></p>
              </div>
            </div>
          )}
          {(slide.kind === 'note' || slide.kind === 'end') && (
            <p style={{ fontSize: 'clamp(13px, 2.1vw, 19px)', lineHeight: 1.6, margin: 0, color: slide.kind === 'end' ? '#BBF7D0' : 'white' }}>
              <RichText text={slide.text} />
            </p>
          )}
        </div>
        {!playing && !finished && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)' }}>
            <Play size={56} weight="fill" color="white" />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '3px', padding: '6px 8px 0' }}>
        {slides.map((s, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => go(i)}
            style={{
              flex: 1, height: '4px', border: 'none', padding: 0, cursor: 'pointer',
              background: i < index ? '#60A5FA' : i === index ? '#2563EB' : '#334155',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px 6px', color: 'white', fontSize: '11px' }}>
        <button type="button" style={btn} onClick={() => go(index - 1)} aria-label="Previous"><SkipBack size={16} weight="fill" /></button>
        {finished ? (
          <button type="button" style={btn} onClick={restart} aria-label="Replay"><ArrowCounterClockwise size={18} /></button>
        ) : (
          <button type="button" style={btn} onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
          </button>
        )}
        <button type="button" style={btn} onClick={() => go(index + 1)} aria-label="Next"><SkipForward size={16} weight="fill" /></button>
        <span style={{ color: '#CBD5E1' }}>{index + 1} / {slides.length}</span>
        <span style={{ flex: 1 }} />
        {canSpeak && (
          <button type="button" style={btn} onClick={() => setVoice((v) => !v)} aria-label={voice ? 'Mute narration' : 'Turn on narration'} title={voice ? 'Mute narration' : 'Turn on narration'}>
            {voice ? <SpeakerHigh size={16} /> : <SpeakerSlash size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}

export default HelpWalkthrough;
